import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Box, Typography, Tooltip, Collapse,
} from "@mui/material";
import * as Icons from "@mui/icons-material";
import { C, LAYOUT } from "../constants/colors";
import { NAV_ITEMS } from "../constants/nav";

function childIsActive(child, location) {
  if (!child?.path) return false;
  if (!location.pathname.startsWith("/planner")) return false;
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab") || "inputs";
  const page = params.get("page") || "basics";
  if (child.tab === "plans") return tab === "plans";
  if (child.tab === "inputs") return tab === "inputs" && page === child.pageId;
  return false;
}

export default function Sidebar({ open, onClose, variant = "permanent" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const onPlanner = location.pathname.startsWith("/planner");
  const [plannerOpen, setPlannerOpen] = useState(onPlanner);
  const [groupOpen, setGroupOpen] = useState({ setup: true, learning: true, practice: false, schedule: false });

  useEffect(() => {
    if (onPlanner) setPlannerOpen(true);
  }, [onPlanner]);

  const go = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const content = (
    <Box sx={{
      width: LAYOUT.sidebarWidth,
      display: "flex",
      flexDirection: "column",
      height: "100%",
      background: C.shell,
      color: C.shellText,
      borderRight: `1px solid ${C.shellBorder}`,
    }}>
      <Toolbar sx={{ minHeight: `${LAYOUT.navbarHeight}px !important` }} />

      <List sx={{ px: 1.25, py: 0.5, flex: 1, overflowY: "auto" }}>
        {NAV_ITEMS.map((item) => {
          const Icon = Icons[item.icon] || Icons.Circle;
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const active = location.pathname === item.path;
          const parentActive = item.key === "planner" ? onPlanner : active;

          return (
            <Box key={item.key} sx={{ mb: 0.5 }}>
              {/* ── Top-level nav row ── */}
              <Tooltip title={item.label} placement="right" disableHoverListener={variant !== "collapsed"}>
                <ListItemButton
                  selected={parentActive && !hasChildren}
                  onClick={() => {
                    if (hasChildren) {
                      setPlannerOpen((o) => !o);
                      if (!onPlanner) go(item.children[0]?.path || item.path);
                    } else {
                      go(item.path);
                    }
                  }}
                  sx={{
                    borderRadius: `${LAYOUT.radiusSm}px`,
                    color: parentActive ? C.primaryDark : C.shellMuted,
                    background: parentActive ? C.shellActiveBg : "transparent",
                    boxShadow: parentActive ? `inset 3px 0 0 ${C.shellRail}` : "none",
                    "&.Mui-selected": { background: C.shellActiveBg, color: C.primaryDark, "&:hover": { background: C.primarySoft } },
                    "&:hover": { background: C.shellAlt, color: C.text },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: parentActive ? C.primary : "inherit" }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 13.5, fontWeight: parentActive ? 700 : 500, letterSpacing: "-0.01em" }}
                  />
                  {hasChildren && (
                    <Icons.ExpandMore sx={{
                      fontSize: 18, color: C.shellMuted,
                      transform: plannerOpen ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s ease",
                    }} />
                  )}
                </ListItemButton>
              </Tooltip>

              {/* ── Ordered planner groups ── */}
              {hasChildren && (
                <Collapse in={plannerOpen} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ pt: 0.25, pb: 0.5, pl: 1 }}>
                    {item.children.map((child) => {
                      if (child.children) {
                        const groupActive = child.children.some((entry) => childIsActive(entry, location));
                        const expanded = groupOpen[child.key] || groupActive;
                        return <Box key={child.key} sx={{ mb: 0.25 }}>
                          <ListItemButton onClick={() => setGroupOpen((prev) => ({ ...prev, [child.key]: !prev[child.key] }))} sx={{ minHeight: 32, borderRadius: `${LAYOUT.radiusSm}px`, pl: 1.5, py: .35, color: groupActive ? C.primaryDark : C.shellMuted, "&:hover": { background: C.shellAlt, color: C.text } }}>
                            <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }} />
                            <Icons.ExpandMore sx={{ fontSize: 17, transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                          </ListItemButton>
                          <Collapse in={expanded} timeout="auto" unmountOnExit><List disablePadding sx={{ pl: 1 }}>
                            {child.children.map((entry) => { const selected = childIsActive(entry, location); return <ListItemButton key={entry.key} selected={selected} onClick={() => go(entry.path)} sx={{ minHeight: 32, borderRadius: `${LAYOUT.radiusSm}px`, mb: .15, py: .25, pl: 1.5, color: selected ? C.primaryDark : C.shellMuted, "&.Mui-selected": { background: C.shellActiveBg, color: C.primaryDark }, "&:hover": { background: C.shellAlt, color: C.text } }}><ListItemText primary={entry.label} primaryTypographyProps={{ fontSize: 12, fontWeight: selected ? 650 : 500 }} /></ListItemButton>; })}
                          </List></Collapse>
                        </Box>;
                      }
                      const childActive = childIsActive(child, location);
                      return (
                        <ListItemButton
                          key={child.key}
                          selected={childActive}
                          onClick={() => go(child.path)}
                          sx={{
                            borderRadius: `${LAYOUT.radiusSm}px`,
                            mb: 0.2,
                            py: 0.4,
                            pl: 1.5,
                            color: childActive ? C.primaryDark : C.shellMuted,
                            background: childActive ? C.shellActiveBg : "transparent",
                            boxShadow: childActive ? `inset 3px 0 0 ${C.primary}` : "none",
                            "&.Mui-selected": {
                              background: C.shellActiveBg,
                              color: C.primaryDark,
                              "&:hover": { background: C.primarySoft },
                            },
                            "&:hover": { background: C.shellAlt, color: C.text },
                          }}
                        >
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: 12.5,
                              fontWeight: childActive ? 600 : 500,
                              letterSpacing: "-0.01em",
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>

      <Box sx={{ p: 2, borderTop: `1px solid ${C.shellBorder}`, background: C.shellAlt }}>
        <Typography sx={{ fontSize: 11, color: C.faint, fontWeight: 600 }}>
          AI Path Builder · {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );

  if (variant === "temporary") {
    return (
      <Drawer variant="temporary" open={open} onClose={onClose} ModalProps={{ keepMounted: true }}>
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: LAYOUT.sidebarWidth,
        flexShrink: 0,
        display: { xs: "none", md: "block" },
        "& .MuiDrawer-paper": {
          width: LAYOUT.sidebarWidth,
          boxSizing: "border-box",
          background: C.shell,
          borderRight: `1px solid ${C.shellBorder}`,
        },
      }}
    >
      {content}
    </Drawer>
  );
}
